import { setActiveSidebarTab } from 'client/actions/actionsManagement'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useWebsiteSettings } from 'client/store'
import { localeNames } from 'common/constants/localeTypes'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { copyWebsitePages } from 'src/editor/api/website-websites-api'
import { useFetchAndSwitchPage } from 'src/editor/hooks/use-fetch-and-switch-page'
import { CreateBlogModal } from 'src/editor/layout/sidebar/pages-management/components/create-blog-modal'
import { CreatePageModal } from 'src/editor/layout/sidebar/pages-management/components/create-page-modal'
import { HorizontalLine } from 'src/editor/layout/sidebar/settings/horizontal-line'
import { FeatureFlagsEnum } from 'src/editor/shared/growthbook/feature-flags-enum'
import { useFeatureFlag } from 'src/editor/shared/growthbook/use-feature-flag'
import { PlusCircleIcon } from 'src/editor/shared/icons/plus-circle-icon'
import {
  GhostIconButton,
  SecondaryButton,
} from 'src/editor/shared/ui-lib/buttons'
import { LanguageSelector } from 'src/editor/shared/ui-lib/language-selector'
import { getLocalesOptions } from 'src/editor/shared/ui-lib/language-selector/utils/get-locales-options'
import { PageElement } from 'src/editor/shared/ui-lib/page-element'
import {
  BadgeColorEnum,
  BadgeSizeEnum,
} from 'src/editor/shared/ui-lib/shared/badge-consts'
import { getLocalesCountByPageSets } from 'src/editor/shared/utils/get-locales-count-by-page-sets'
import { getWebsiteLayoutPageIdForLocale } from 'src/editor/shared/utils/get-website-layout-page-id'
import {
  setCurrentWebsiteLocale,
  setWebsiteLayoutByLocale,
} from 'src/editor/store/website-settings/website-settings-actions'
import {
  getCurrentWebsiteLocale,
  getWebsiteId,
  getWebsiteLayouts,
  getWebsiteLocale,
  getWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-selector'
import { SidebarTabEnum } from 'src/shared/enums/website-enums'
import { DuplicatePagesBanner } from './components/duplicate-pages-banner'
import { PageElementWithActions } from './components/page-element-with-actions'

export const PagesManagement = () => {
  const { t } = useTranslation()

  const [isCreatePageModalVisible, setCreatePageModalVisible] = useState(false)
  const [isCreateBlogModalVisible, setCreateBlogModalVisible] = useState(false)

  const dispatch = useAppDispatch()

  const websiteLocale = useWebsiteSettings(getWebsiteLocale) as PageLocaleEnum
  const currentWebsiteLocale = useWebsiteSettings(
    getCurrentWebsiteLocale,
  ) as PageLocaleEnum
  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const websiteLocales = useWebsiteSettings(getWebsiteLocales)
  const websiteLayouts = useWebsiteSettings(getWebsiteLayouts) || []

  const isWebsiteBlogEnabled = useFeatureFlag(FeatureFlagsEnum.WebsiteBlog)

  const { data, isFetching } = websitePagesQuery.useQuery({
    queryFnArgs: [websiteId],
  })

  const localesCount = useMemo(
    () => getLocalesCountByPageSets(data?.pages ?? []),
    [data?.pages],
  )

  const localeOptions = useMemo(
    () =>
      getLocalesOptions({
        t,
        localeList: websiteLocales,
        setAdditionalProperties: locale => ({
          optionBadgeProps: {
            color: BadgeColorEnum.Primary,
            size: BadgeSizeEnum.Small,
            children: t(
              'website_settings.sidebar.pages_management.language_switcher.badge_option_icu',
              {
                count: localesCount[locale] ?? 0,
              },
            ),
          },
        }),
      }),
    [localesCount, t, websiteLocales],
  )

  const { fetcher: fetcherCopyPages, isLoading: isLoadingCopyPages } =
    useMutationWithValidation({
      requestFunction: copyWebsitePages,
      successToastMessage: t('website_settings.copy_pages.copied_successfully'),
      invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
    })

  const {
    urlPageId,
    loadingPageId,
    checkPageModification,
    renderUnsavedPageModal,
  } = useFetchAndSwitchPage()

  return (
    <>
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="inline-flex basis-full items-center gap-3">
          <span className="text-heading-4">{t('pages_management.title')}</span>
          <LanguageSelector
            withoutWrapper
            className="basis-full"
            selectedLocales={currentWebsiteLocale}
            options={localeOptions}
            onChange={locale =>
              dispatch(setCurrentWebsiteLocale(locale as PageLocaleEnum))
            }
          />
        </div>
        <GhostIconButton
          onClick={() => setCreatePageModalVisible(true)}
          icon={<PlusCircleIcon />}
          title={t('pages_management.create_page')}
        />
      </div>
      <div className="flex flex-col gap-1">
        {isFetching && !data?.pages?.length && (
          <Skeleton height={64} count={3} />
        )}
        {data?.pages?.length &&
          data.pages.map(({ id, pageType, locales }) => {
            const websitePage = locales.find(
              ({ locale }) => locale === currentWebsiteLocale,
            )
            return (
              websitePage && (
                <PageElementWithActions
                  key={id}
                  locales={locales}
                  isLoading={loadingPageId === websitePage.pageContentId}
                  websiteId={websiteId}
                  pageType={pageType}
                  websitePageSetId={id}
                  websitePageId={websitePage.id}
                  websitePageLocale={websitePage.locale}
                  name={websitePage?.name ?? ''}
                  pageId={websitePage.pageContentId}
                  isPublished={websitePage?.active ?? false}
                  isActive={urlPageId === websitePage.pageContentId.toString()}
                  onClick={() => {
                    const layoutPageId = getWebsiteLayoutPageIdForLocale({
                      layouts: websiteLayouts,
                      currentLocale: websitePage.locale,
                    })
                    if (layoutPageId) {
                      dispatch(setWebsiteLayoutByLocale(websitePage.locale))
                      checkPageModification(
                        websitePage.pageContentId,
                        layoutPageId,
                      )
                    }
                  }}
                />
              )
            )
          })}
        {!isFetching && !localesCount[currentWebsiteLocale] && (
          <div className="flex flex-col gap-3">
            <span className="text-M text-textTertiary">
              {t('pages_management.no_pages_in_language_icu', {
                language: t(localeNames[websiteLocale]),
              })}
            </span>
            <SecondaryButton
              isLoading={isLoadingCopyPages}
              onClick={() => {
                fetcherCopyPages({
                  id: websiteId,
                  locale: websiteLocale,
                })
              }}
            >
              {t('pages_management.copy_pages.button')}
            </SecondaryButton>
          </div>
        )}
        {isWebsiteBlogEnabled && !isFetching && (
          <>
            <HorizontalLine className="my-1 bg-bgTertiary" />
            <PageElement
              name={t('entity_settings.blog.title')}
              pageType={PageTypeEnum.BlogHome}
              endElement={
                <GhostIconButton
                  className="shrink-0"
                  onClick={() => setCreateBlogModalVisible(true)}
                  icon={<PlusCircleIcon />}
                  title={t('pages_management.create_page')}
                />
              }
              onClick={() => {
                dispatch(
                  setActiveSidebarTab(SidebarTabEnum.WebsiteBlogSettings),
                )
              }}
            />
          </>
        )}
      </div>
      <CreatePageModal
        isVisible={isCreatePageModalVisible}
        onClose={() => setCreatePageModalVisible(false)}
        websiteId={websiteId}
        currentWebsiteLocale={currentWebsiteLocale}
      />
      <CreateBlogModal
        isVisible={isCreateBlogModalVisible}
        onClose={() => setCreateBlogModalVisible(false)}
        websiteId={websiteId}
        currentWebsiteLocale={currentWebsiteLocale}
      />
      <DuplicatePagesBanner
        localesCount={localesCount}
        buttonProps={{
          isLoading: isLoadingCopyPages,
          onClick: () => {
            fetcherCopyPages({ id: websiteId, locale: currentWebsiteLocale })
          },
        }}
      />
      {renderUnsavedPageModal()}
    </>
  )
}
